/* Shopify is the source of truth. This module never changes a cart. */
(function (root) {
  'use strict';
  function option(plan, name) {
    const matches = (plan?.options || []).filter(value => value.name === name);
    return matches.length === 1 ? matches[0].value : null;
  }
  function terms(plan) {
    const quantity = Number(option(plan, 'Product Quantity'));
    const frequency = option(plan, 'Order Frequency and Unit');
    return [1, 2, 3].includes(quantity) && /^[1-3]-month$/.test(frequency || '')
      ? { quantity, months: Number(frequency[0]) } : null;
  }
  function productData(product, variantId) {
    const variant = product?.variants?.find(value => value.id === variantId);
    if (!variant) return null;
    const definitions = (product.selling_plan_groups || []).flatMap(group => group.selling_plans || []);
    const plans = (variant.selling_plan_allocations || []).flatMap(allocation => {
      const matches = definitions.filter(plan => plan.id === allocation.selling_plan_id);
      return matches.length === 1 ? [{ ...matches[0], price: allocation.price, compare_at_price: allocation.compare_at_price }] : [];
    });
    // The explicit quantity/month option schema identifies tier products, not a test ID.
    if (!plans.some(terms)) return null;
    return { variant, plans };
  }
  function resolve(data, quantity, months) {
    if (!Number.isSafeInteger(quantity) || quantity < 1) return null;
    const matches = (data?.plans || []).filter(plan => {
      const value = terms(plan);
      return value?.quantity === Math.min(quantity, 3) && value.months === months;
    });
    const plan = matches.length === 1 ? matches[0] : null;
    return plan && Number.isSafeInteger(plan.id) && plan.id > 0 &&
      Number.isSafeInteger(plan.price) && plan.price >= 0 && plan.recurring_deliveries === true ? plan : null;
  }
  function state(item, data) {
    const current = item.selling_plan_allocation?.selling_plan;
    if (!current) return { mode: 'onetime', quantity: item.quantity, months: null };
    const value = terms(current);
    if (!value || !data.plans.some(plan => plan.id === current.id)) return null;
    return { mode: 'subscription', quantity: item.quantity, months: value.months };
  }
  // Both catalog handles occur in the existing packet integration. Presentation only.
  function isPacket(item) {
    return ['rest-digest-to-go-5x-single-serve-packets', '5-single-serve-packets'].includes(item.handle);
  }
  function properties(item) {
    return JSON.stringify(Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value != null).sort(([a], [b]) => a.localeCompare(b)));
  }
  function group(cart, item, data, months) {
    return cart.items.filter(other => other.variant_id === item.variant_id && properties(other) === properties(item) &&
      state(other, data)?.months === months);
  }
  function change(cart, key, data, action, value) {
    const item = cart.items.find(value => value.key === key);
    if (!item) throw new Error('Your cart changed. Please review it and try again.');
    if (item.variant_id !== data.variant.id) throw new Error('The product could not be verified. Please refresh.');
    const current = state(item, data);
    if (!current) throw new Error('This subscription plan could not be verified.');
    const next = { ...current };
    if (action === 'quantity') next.quantity += Number(value);
    else if (action === 'months') next.months = Number(value);
    else if (action === 'subscribe') { next.mode = 'subscription'; next.months = Math.min(next.quantity, 3); }
    else throw new Error('Unknown cart action.');
    if (!Number.isSafeInteger(next.quantity) || next.quantity < 0) throw new Error('Please choose a valid quantity.');
    const rule = item.quantity_rule || {};
    if (next.quantity && ((rule.min && next.quantity < rule.min) || (rule.max && next.quantity > rule.max) ||
      (rule.increment && next.quantity % rule.increment))) throw new Error('This quantity is not available.');
    const payload = { id: key, quantity: next.quantity }; // By key: line numbers shift when Rebuy changes its gift.
    if (!next.quantity) return payload;
    if (next.mode === 'onetime') {
      if (data.variant.requires_selling_plan !== false) throw new Error('One-time purchase is not available.');
      payload.selling_plan = null;
    } else {
      const total = next.quantity + group(cart, item, data, next.months).filter(other => other.key !== key).reduce((sum, other) => sum + other.quantity, 0);
      const plan = resolve(data, total, next.months);
      if (!plan) throw new Error('That quantity and delivery interval are not available.');
      payload.selling_plan = plan.id;
    }
    // Deliberately omit properties: Shopify otherwise replaces the whole properties object.
    return payload;
  }
  function reward(current) {
    if (!current) return { message: 'Subscribe & save up to 20% on every order.', cta: '', progress: 0, labels: ['1 box · 10% off', '2 boxes · free shipping + 15%', '3 boxes · 20% off'] };
    const q = current.quantity;
    if (current.mode === 'subscription') {
      const messages = ['', 'Add a 2nd box for free shipping + 15% off', 'Add 1 more box for 20% off', "You've unlocked free shipping, 20% off"];
      return { message: messages[Math.min(q, 3)], cta: q === 1 ? 'Add a 2nd box — free shipping + save 15%' : q === 2 ? 'Add a 3rd box — save 20%' : '', progress: Math.min(q / 3, 1), labels: ['1 box · 10% off', '2 boxes · free shipping + 15%', '3 boxes · 20% off'] };
    }
    return { message: q < 2 ? 'Add 1 more box to unlock free shipping.' : q === 2 ? 'Add 2 more boxes for a free gift.' : q === 3 ? 'Add 1 more box for a free gift.' : "You've unlocked free shipping + a free gift.", cta: '', progress: Math.min(q / 4, 1), labels: ['1 box', '2 boxes · free shipping', '4 boxes · free gift'] };
  }
  function subscribed(cart, data) {
    return cart.items.filter(item => item.variant_id === data.variant.id)
      .map(item => ({ item, current: state(item, data) })).filter(entry => entry.current?.mode === 'subscription');
  }
  function cadences(cart, data) { return new Set(subscribed(cart, data).map(entry => entry.current.months)); }
  // The one next correction for this tea's subscription lines, or null when they follow
  // the rules. Lines are addressed by key: Rebuy may add or remove its gift at any moment,
  // which shifts line numbers (Shopify accepts a plan change by key; verified Oct 10).
  // 1. One subscription per cart (Natazia, Oct 6): every subscribed box shares one delivery
  //    interval. The interval the shopper just chose wins, else the newest line's (Shopify
  //    lists the newest line first).
  // 2. One line per subscription: lines that differ only in properties (a Rebuy widget's
  //    tracking) would become separate subscriptions the pack can't be paired with; they
  //    move onto the newest line in one request.
  // 3. Each line on the plan for its quantity tier.
  function nextChange(cart, data, prefer) {
    const entries = subscribed(cart, data);
    const months = new Set(entries.map(entry => entry.current.months));
    if (months.size > 1) {
      const target = months.has(prefer) ? prefer : entries[0].current.months;
      const { item } = entries.find(entry => entry.current.months !== target);
      const plan = resolve(data, item.quantity, target);
      if (!plan) throw new Error('The combined subscription quantity is unavailable. Please review your cart.');
      return { endpoint: 'cart/change.js', body: { id: item.key, quantity: item.quantity, selling_plan: plan.id } };
    }
    // Identical lines (same plan and properties) are Shopify's own split, e.g. a discount on
    // some units: one subscription already, and merging would only recreate them.
    const identity = item => item.selling_plan_allocation.selling_plan.id + properties(item);
    if (new Set(entries.map(entry => identity(entry.item))).size > 1) {
      const total = entries.reduce((sum, entry) => sum + entry.item.quantity, 0);
      return { endpoint: 'cart/update.js', body: { updates: Object.fromEntries(entries.map((entry, index) => [entry.item.key, index ? 0 : total])) } };
    }
    const total = entries.reduce((sum, entry) => sum + entry.item.quantity, 0);
    for (const { item, current } of entries) {
      const plan = resolve(data, total, current.months);
      if (!plan) throw new Error('The combined subscription quantity is unavailable. Please review your cart.');
      if (item.selling_plan_allocation.selling_plan.id !== plan.id) return { endpoint: 'cart/change.js', body: { id: item.key, quantity: item.quantity, selling_plan: plan.id } };
    }
    return null;
  }
  async function normalize(cart, data, request, { prefer } = {}) {
    const passes = cart.items.length + 3; // Fixed up front: merges shrink the cart.
    for (let pass = 0; pass < passes; pass++) {
      const next = nextChange(cart, data, prefer);
      if (!next) return cart;
      cart = await request(next.endpoint, next.body);
    }
    throw new Error('Your cart changed during the update. Please review it before trying again.');
  }
  const api = { terms, productData, resolve, state, isPacket, change, reward, group, cadences, nextChange, normalize };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.TeaCartModel = Object.freeze(api);
})(typeof window === 'undefined' ? globalThis : window);
